import { ReservationUpdateMinimalParamsType, ScheduleNewReservationParamsType } from "src/types";
import axiosClient from "./axiosClient";

// Define interfaces for parameters
interface IReservationListParams {
  room_ids?: number[];
  start_date?: string;
  end_date?: string;
}

interface ICreateRoomParams {
  name: string;
  capacity: number;
}

interface IAddParticipantParams {
  reservation_id: number;
  user_id: number;
  required: boolean;
}

interface IUpdateParticipantParams {
  id: number;
  required?: boolean;
}

// Define new message-related interfaces
interface IMessageListParams {
  creator_id?: number;
  reservation_id?: number;
}

interface ICreateMessageParams {
  reservation_id: number | null;
  temp_reservation_id?: number;
  content?: string;
  file_url?: string;
}

interface IUpdateMessageParams {
  id: number;
  content?: string;
  file_url?: string;
}

interface ICreateListParams {
    type: 'favorite' | 'default';
    participants: number[];
    name?: string;
}

interface IUpdateListParams {
    id: number;
    type?: 'favorite' | 'default';
    participants?: number[];
    name?: string;
}

export const scheduleApi = {
  // Room operations
  createRoom: (params: ICreateRoomParams) => {
    const url = `/api/schedule/room`;
    return axiosClient.post(url, params);
  },

  getRooms: () => {
    const url = `/api/schedule/rooms`;
    return axiosClient.get(url);
  },

  // Type operations
  getTypes: () => {
    const url = `/api/schedule/types`;
    return axiosClient.get(url);
  },

  // Reservation operations
  getReservations: (params?: IReservationListParams) => {
    let url = `/api/schedule/reservations`;
    if (params) {
      const queryParams = new URLSearchParams();
      // Only support one room_id in the query string
      if (params.room_ids !== undefined && params.room_ids.length > 0) {
        queryParams.append('room_id', params.room_ids[0].toString());
      }
      if (params.room_ids !== undefined) {
        queryParams.append('room_ids', params.room_ids.toString());
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }
    return axiosClient.get(url);
  },
  createReservation: (params: ScheduleNewReservationParamsType) => {
    const url = `/api/schedule/reservations`;
    return axiosClient.post(url, params);
  },
  updateReservation: (params: ReservationUpdateMinimalParamsType) => {
    const url = `/api/schedule/reservations/${params.id}`;
    const { id, ...updateData } = params;
    return axiosClient.put(url, updateData);
  },
  deleteReservation: (id: number) => {
    const url = `/api/schedule/reservations/${id}`;
    return axiosClient.delete(url);
  },
  cancelReservation: (id: number) => {
    const url = `/api/schedule/reservations/${id}/cancel`;
    return axiosClient.put(url);
  },

  // Participant operations
  addParticipant: (params: IAddParticipantParams) => {
    const url = `/api/schedule/participant`;
    return axiosClient.post(url, params);
  },
  updateParticipant: (params: IUpdateParticipantParams) => {
    const url = `/api/schedule/participant/${params.id}`;
    const { id, ...updateData } = params;
    return axiosClient.put(url, updateData);
  },
  deleteParticipant: (id: number) => {
    const url = `/api/schedule/participant/${id}`;
    return axiosClient.delete(url);
  },

  // Message operations
  getMessages: (params?: IMessageListParams) => {
    let url = `/api/schedule/messages`;
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.creator_id !== undefined) {
        queryParams.append('creator_id', params.creator_id.toString());
      }
      if (params.reservation_id !== undefined) {
        queryParams.append('reservation_id', params.reservation_id.toString());
      }
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }
    return axiosClient.get(url);
  },
  getReservationMessages: (reservationId: number) => {
    const url = `/api/schedule/reservations/${reservationId}/messages`;
    return axiosClient.get(url);
  },
  createMessage: (params: ICreateMessageParams) => {
    const url = `/api/schedule/messages`;
    return axiosClient.post(url, params);
  },
  updateMessage: (params: IUpdateMessageParams) => {
    const url = `/api/schedule/messages/${params.id}`;
    const { id, ...updateData } = params;
    return axiosClient.put(url, updateData);
  },
  deleteMessage: (id: number) => {
    const url = `/api/schedule/messages/${id}`;
    return axiosClient.delete(url);
  },
  requestFileUrl: (params: { filename: string, file_type: string, acl: string }) => {
    const url = `/api/schedule/file/upload-url`;
    return axiosClient.post(url, params);
  },
  deleteFile: (params: { file_path: string }) => {
    const url = `/api/schedule/file`;
    return axiosClient.delete(url, { data: params });
  },

  getLists: () => {
      const url = `/api/schedule/lists`;
      return axiosClient.get(url);
  },
  createList: (params: ICreateListParams) => {
      const url = `/api/schedule/lists`;
      return axiosClient.post(url, params);
  },
  updateList: (params: IUpdateListParams) => {
      const url = `/api/schedule/lists/${params.id}`;
      const { id, ...updateData } = params;
      return axiosClient.put(url, updateData);
  },
  deleteList: (id: number) => {
      const url = `/api/schedule/lists/${id}`;
      return axiosClient.delete(url);
  },
};